/**
 * Center Team Message Component
 * Shared notepad for broadcasting messages with individual attribution
 * Auto-saves and syncs in real-time with beep notifications
 */
import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, Send, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { useTeamMessages } from "@/contexts/TeamMessageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CenterTeamMessage = ({ date, currentUserEmail, currentUserName }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [expanded, setExpanded] = useState(true);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(null); // Track which message is being deleted
  const { clearNewMessageFlag, notifyMessageSent } = useTeamMessages();
  
  // Track last known state for beep notification
  const lastKnownCountRef = useRef(0);
  const lastKnownTimestampRef = useRef(null);
  const lastUpdatedByRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  // Parse messages from stored string format: "message|||name|||timestamp|||email"
  const parseMessages = (rawMessage) => {
    if (!rawMessage) return [];
    
    const lines = rawMessage.split('\n').filter(line => line.trim());
    return lines.map((line, index) => {
      const parts = line.split('|||');
      if (parts.length >= 3) {
        return {
          id: index,
          text: parts[0],
          name: parts[1],
          time: parts[2],
          email: parts[3] || '' // Email for sender identification
        };
      }
      // Legacy format - just plain text
      return {
        id: index,
        text: line,
        name: 'Unknown',
        time: '',
        email: ''
      };
    });
  };

  // Format messages back to storage format
  const formatMessagesForStorage = (messagesArray) => {
    return messagesArray.map(m => `${m.text}|||${m.name}|||${m.time}|||${m.email}`).join('\n');
  };

  // Fetch current messages
  const fetchMessages = async (checkForBeep = false) => {
    try {
      const response = await axios.get(`${API}/team-messages/today?date=${date}`);
      const data = response.data;
      
      if (data.message !== undefined) {
        const parsed = parseMessages(data.message);
        const currentUpdatedBy = data.updatedBy; // Email of last person who updated
        
        // NOTE: Beep is handled globally by TeamMessageContext - no duplicate beep here
        // Just update the message display
        
        setMessages(parsed);
        lastKnownCountRef.current = parsed.length;
        lastKnownTimestampRef.current = data.updatedAt;
        lastUpdatedByRef.current = currentUpdatedBy;
      }
      
      // Mark initial load as complete after first successful fetch
      isInitialLoadRef.current = false;
    } catch (error) {
      console.error("Error fetching team messages:", error);
    }
  };

  // Send new message
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSending(true);
    try {
      const now = new Date().toISOString();
      const userName = currentUserName || currentUserEmail?.split('@')[0] || 'Unknown';
      const userEmail = currentUserEmail || '';
      
      // Add new message to existing messages with email for identification
      const newMsg = {
        id: messages.length,
        text: newMessage.trim(),
        name: userName,
        time: now,
        email: userEmail
      };
      
      const updatedMessages = [...messages, newMsg];
      const formattedMessage = formatMessagesForStorage(updatedMessages);
      
      await axios.post(`${API}/team-messages/today`, {
        date: date,
        message: formattedMessage
      });
      
      setMessages(updatedMessages);
      setNewMessage("");
      
      // Update refs so we don't beep ourselves
      lastKnownCountRef.current = updatedMessages.length;
      lastUpdatedByRef.current = userEmail;
      
      // Notify global context so TeamMessageContext doesn't beep for our own message
      notifyMessageSent(updatedMessages.length);
      
      toast.success("Message sent to all team members");
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    }
    setSending(false);
  };

  // Delete a message
  const handleDeleteMessage = async (indexToDelete) => {
    setDeleting(indexToDelete);
    try {
      const updatedMessages = messages.filter((_, index) => index !== indexToDelete);
      const formattedMessage = formatMessagesForStorage(updatedMessages);
      
      await axios.post(`${API}/team-messages/today`, {
        date: date,
        message: formattedMessage,
        skip_sms: true
      });
      
      setMessages(updatedMessages);
      lastKnownCountRef.current = updatedMessages.length;
      
      // Notify global context so TeamMessageContext stays in sync
      notifyMessageSent(updatedMessages.length);
      
      toast.success("Message deleted");
    } catch (error) {
      console.error("Error deleting message:", error);
      toast.error("Failed to delete message");
    }
    setDeleting(null);
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Initial fetch and polling
  useEffect(() => {
    if (!date) return;

    // Reset for new date
    isInitialLoadRef.current = true;
    lastKnownCountRef.current = 0;
    
    // Initial fetch (no beep on first load)
    fetchMessages(false);
    
    // Poll every 3 seconds for updates from others
    const pollInterval = setInterval(() => {
      fetchMessages(true); // Check for beep on poll
    }, 3000);
    
    return () => clearInterval(pollInterval);
  }, [date, currentUserEmail]);

  // Clear new message flag when viewing this component
  useEffect(() => {
    clearNewMessageFlag();
  }, [clearNewMessageFlag]);

  // Format time for display
  const formatTime = (isoString) => {
    if (!isoString) return "";
    try {
      return format(parseISO(isoString), 'h:mm a');
    } catch {
      return "";
    }
  };

  return (
    <Card 
      className="mb-6 border-0" 
      data-testid="team-message-card"
      style={{
        background: 'rgba(255,255,255,0.75)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        border: '1px solid rgba(59,130,246,0.2)'
      }}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg text-blue-800 flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Center Team Message
            {messages.length > 0 && (
              <span className="text-sm bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full">
                {messages.length}
              </span>
            )}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="text-blue-600 hover:text-blue-800"
          >
            {expanded ? <X className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>
      
      {expanded && (
        <CardContent className="space-y-3">
          {/* Messages Display */}
          {messages.length > 0 ? (
            <div className="bg-white rounded-lg border border-blue-200 p-3 max-h-[200px] overflow-y-auto space-y-2">
              {messages.map((msg, index) => (
                <div key={index} className="border-b border-gray-100 pb-2 last:border-0 last:pb-0 group">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-gray-800 text-sm">{msg.text}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        — {msg.name}{msg.time ? `, ${formatTime(msg.time)}` : ''}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 text-red-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => handleDeleteMessage(index)}
                      disabled={deleting === index}
                      title="Delete message"
                      data-testid={`delete-message-${index}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-blue-200 p-4 text-center">
              <p className="text-sm text-gray-500">No messages today. Send one to notify all rooms!</p>
            </div>
          )}

          {/* Message Input */}
          <div className="flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message to broadcast..."
              className="border-blue-200 bg-white flex-1"
              data-testid="team-message-input"
            />
            <Button
              onClick={handleSendMessage}
              disabled={sending || !newMessage.trim()}
              className="bg-blue-500 hover:bg-blue-600 text-white px-3"
              data-testid="send-message-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default CenterTeamMessage;
